import * as names from "@/store/names";

/**
 * E54 · Guardado de las preguntas que propuso la IA.
 *
 * Vive acá, y no dentro del modal, por la misma razón que `createDesignEvaluation.js` en el
 * Diseño Instruccional: el modal es la conversación con el usuario y esto es la política de
 * guardado. La pauta va a necesitar su gemelo, y el botón de "completar" también va a entrar
 * por este camino; dos copias del armado del payload se separan con el tiempo y una de las dos
 * termina guardando preguntas que la vista no puede editar.
 *
 * Lo único que hace de distinto al formulario manual es el orden: crea en secuencia y nunca
 * toca lo que ya existe. Las preguntas nuevas se agregan al final, igual que agregar una a
 * mano, para que una generación no pueda pisar lo que alguien ya corrigió.
 */

const KIND_REDACTION = "redaction";

/**
 * El payload común a los dos tipos de pregunta.
 *
 * Los Indicadores de Logro viajan en `evaluation_criterias_micro` (el arreglo) y NO en
 * `evaluation_criteria_micro` (la FK suelta): es el campo que usa el formulario de preguntas
 * desde que el selector único quedó comentado, y es el que lee la Tabla de Especificaciones.
 * Poner la FK sola dejaría la pregunta amarrada a medias: visible en la pregunta, ausente de
 * la cobertura.
 *
 * Son VARIOS a propósito: una pregunta integradora mide dos Indicadores a la vez, el modelo
 * lo permite y el formulario manual también. Obligarla a declarar uno dejaría al otro sin
 * cobertura.
 */
function basePayload(
  question,
  { test_id, segment_id, order, dimension_driven = false }
) {
  return {
    title: question.title,
    description: question.description || "",
    // Vacía a propósito: la IA no redacta la respuesta esperada. Es lo que el
    // docente usa para corregir y lo que decide qué cuenta como correcto; con
    // una propuesta puesta, se corrige con el criterio del modelo.
    proposed_answer: "",
    max_score: question.max_score,
    order,
    test: test_id,
    segment: segment_id,
    evaluation_criteria_micro: null,
    // Donde se evalúa a través de la Dimensión, la pregunta se cuelga de ella y los
    // Indicadores quedan vacíos: así están las preguntas que esa institución crea a mano
    // —su formulario esconde el selector de Indicadores— y así la cobertura se cuenta por
    // un solo camino. Llenar los dos la contaría dos veces.
    evaluation_criterias_micro: dimension_driven
      ? []
      : question.evaluation_criterias_micro || [],
    linked_rubric_criteria: question.linked_rubric_criteria || null,
  };
}

/**
 * Crea una pregunta de Desarrollo.
 *
 * `subtype` se deja sin declarar a propósito: el modelo tiene Breve y Extensa, pero el
 * formulario sólo ofrece subtipos para las de selección, así que una pregunta nacida con
 * subtipo quedaría con un dato que nadie puede ver ni cambiar. El largo máximo sí se declara,
 * porque ése el formulario lo edita.
 */
async function createRedactionQuestion(vm, question, placement) {
  return vm.$store.dispatch(names.POST_NEW_REDACTION_QUESTION, {
    ...basePayload(question, placement),
    max_answer_length_type: question.max_answer_length_type,
    max_answer_length: question.max_answer_length,
  });
}

/**
 * Crea una pregunta de Selección y sus alternativas.
 *
 * Las alternativas van una por una y después de la pregunta porque cuelgan de su id. El
 * puntaje de cada una lo repartió el backend al validar: las correctas se llevan el puntaje
 * de la pregunta en partes iguales y las incorrectas quedan en 0, que es cómo este modelo
 * marca la correcta.
 *
 * Los mensajes de retroalimentación por acierto y por error quedan vacíos a propósito: la IA
 * ya no los redacta. Eran dos textos más por pregunta —el gasto crecía con cada una— y son
 * justo lo que el docente escribe mejor y distinto para su curso.
 */
async function createMultipleChoiceQuestion(vm, question, placement) {
  const saved = await vm.$store.dispatch(
    names.POST_NEW_MULTIPLE_CHOICE_QUESTION,
    {
      ...basePayload(question, placement),
      subtype: question.subtype,
      how_many_selected: question.how_many_selected,
      automated_score_type: question.automated_score_type,
    }
  );
  if (!saved) return null;

  for (const option of question.options || []) {
    await vm.$store.dispatch(names.POST_NEW_MULTIPLE_CHOICE_QUESTION_OPTION, {
      title: option.title,
      description: option.description || "",
      score: option.score,
      order: option.order,
      multiple_choice_question: saved.id,
    });
  }
  return saved;
}

/**
 * Guarda las preguntas aceptadas, en el orden en que se muestran.
 *
 * @param vm            el componente que llama (para $store)
 * @param test_id       el Instrumento
 * @param segment_id    el Segmento, si la zona que llamó pertenece a uno
 * @param questions     las preguntas aceptadas, como las devolvió el builder
 * @param start_order   el `order` de la primera: va al final de lo que ya existe
 * @param on_saved      callback por pregunta guardada, para el avance en pantalla
 *
 * Devuelve las preguntas que quedaron creadas. Una que falle no corta las demás: el usuario
 * aceptó un conjunto, y dejarlo a medias sin decir cuáles entraron es peor que guardar las
 * que se pudieron y mostrar el resto como pendiente.
 */
export async function saveAiQuestions(
  vm,
  {
    test_id,
    segment_id = null,
    questions,
    start_order = 1,
    on_saved = null,
    dimension_driven = false,
  }
) {
  const created = [];
  let order = start_order;
  for (const question of questions || []) {
    const placement = { test_id, segment_id, order, dimension_driven };
    try {
      const saved =
        question.kind === KIND_REDACTION
          ? await createRedactionQuestion(vm, question, placement)
          : await createMultipleChoiceQuestion(vm, question, placement);
      if (saved) {
        created.push({ ...question, id: saved.id });
        order += 1;
        if (on_saved) on_saved(created.length);
      }
    } catch (error) {
      // Se sigue con la próxima: el detalle del fallo ya lo reportó `restful`.
      continue;
    }
  }
  return created;
}

/**
 * E55 · Aplica a una pregunta YA guardada la versión que escribió la IA.
 *
 * Reemplaza lo que la pregunta DICE y conserva lo que la ubica y lo que la
 * puntúa: su Indicador de Logro, su tipo, su puntaje, su orden y su segmento.
 * Es la misma política que `redoPieceWithAi` en el Diseño Instruccional, y es
 * lo que separa "quiero otra redacción" de "quiero otra pregunta": sin esa
 * distinción, regenerar movería la cobertura del instrumento sin que nadie lo
 * pida, y eso no se ve —queda incoherente con apariencia de correcto.
 *
 * En las de selección las alternativas sí se reemplazan: son parte de lo que la
 * pregunta dice. El backend sólo deja regenerar una pregunta sin respuestas de
 * evaluados, así que no hay marcas de nadie que perder.
 *
 * @param vm          el componente que llama (para $store)
 * @param question    la pregunta guardada, como la tiene el store
 * @param kind        "redaction" | "multiple_choice"
 * @param fresh       la pregunta que devolvió el builder
 * @param option_ids  los ids de las alternativas actuales, a reemplazar
 */
export async function applyAiQuestionRedo(
  vm,
  { question, kind, fresh, option_ids = [] }
) {
  if (kind === KIND_REDACTION) {
    return vm.$store.dispatch(names.PATCH_NEW_REDACTION_QUESTION, {
      new_redaction_question_id: question.id,
      // `proposed_answer` no se toca: la IA no la escribe, así que regenerar
      // conserva la respuesta esperada que el docente haya redactado.
      item: {
        title: fresh.title,
        description: fresh.description || "",
        max_answer_length_type: fresh.max_answer_length_type,
        max_answer_length: fresh.max_answer_length,
      },
    });
  }

  // Los mensajes de retroalimentación y la respuesta esperada NO se tocan: la IA no
  // los escribe, así que regenerar conserva lo que el docente haya redactado a mano.
  // `how_many_selected` sí, porque las alternativas se reemplazan y con ellas su marca:
  // dejar el viejo describiría una respuesta que ya no existe.
  const saved = await vm.$store.dispatch(
    names.PATCH_NEW_MULTIPLE_CHOICE_QUESTION,
    {
      new_multiple_choice_question_id: question.id,
      item: {
        title: fresh.title,
        description: fresh.description || "",
        how_many_selected: fresh.how_many_selected,
      },
    }
  );

  // Las viejas se van antes de que entren las nuevas: compartirían el `order` y
  // la pregunta quedaría con las dos tandas mezcladas.
  for (const option_id of option_ids) {
    await vm.$store.dispatch(
      names.DELETE_NEW_MULTIPLE_CHOICE_QUESTION_OPTION,
      option_id
    );
  }
  for (const option of fresh.options || []) {
    await vm.$store.dispatch(names.POST_NEW_MULTIPLE_CHOICE_QUESTION_OPTION, {
      title: option.title,
      description: option.description || "",
      score: option.score,
      order: option.order,
      multiple_choice_question: question.id,
    });
  }
  return saved;
}
